import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { auth } from '../../../config/firebase';
import {
  DollarSign,
  Calendar,
  Users,
  FileText,
  Download,
  CheckCircle,
  Clock,
  XCircle,
  AlertCircle,
  TrendingUp,
  Filter,
  Search,
  RefreshCw,
  Play,
  Eye,
  ChevronDown,
  ChevronUp
} from 'lucide-react';
import './PayrollTab.css';

const RAW_API_URL = process.env.REACT_APP_API_BASE_URL || process.env.REACT_APP_API_URL || 'http://localhost:3001/api';
const API_BASE_URL = RAW_API_URL.replace(/\/+$/, '').endsWith('/api')
  ? RAW_API_URL.replace(/\/+$/, '')
  : `${RAW_API_URL.replace(/\/+$/, '')}/api`;

const PayrollTab = ({ hotelId, token }) => {
  const resolvedToken = (
    token ||
    localStorage.getItem('authToken') ||
    sessionStorage.getItem('authToken') ||
    localStorage.getItem('token') ||
    sessionStorage.getItem('token')
  );

  const sanitizedToken = typeof resolvedToken === 'string'
    ? resolvedToken.replace(/^"+|"+$/g, '').trim()
    : '';

  const getAuthConfig = async () => {
    try {
      const currentUser = auth.currentUser;
      if (currentUser) {
        const idToken = await currentUser.getIdToken(false);
        if (idToken) {
          return { headers: { Authorization: `Bearer ${idToken}` } };
        }
      }
    } catch (_) {
    }

    if (sanitizedToken) {
      return {
        headers: {
          Authorization: sanitizedToken.startsWith('Bearer ')
            ? sanitizedToken
            : `Bearer ${sanitizedToken}`
        }
      };
    }

    return {};
  };

  // Data state
  const [payrollRuns, setPayrollRuns] = useState([]);
  const [payslips, setPayslips] = useState([]);
  const [staffSalaries, setStaffSalaries] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [selectedRun, setSelectedRun] = useState(null);
  
  // UI state
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [activeView, setActiveView] = useState('runs'); // runs, payslips, salaries
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedDepartment, setSelectedDepartment] = useState('all');
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [expandedPayslip, setExpandedPayslip] = useState(null);
  
  // Modal state
  const [showRunModal, setShowRunModal] = useState(false);
  const [showSalaryModal, setShowSalaryModal] = useState(false);
  const [editingStaff, setEditingStaff] = useState(null);
  
  // Form state
  const [runFormData, setRunFormData] = useState({
    period_start: '',
    period_end: '',
    department_id: ''
  });
  
  const [salaryFormData, setSalaryFormData] = useState({
    base_salary: '',
    allowances: '',
    deductions: '',
    effective_date: ''
  });

  // Fetch initial data
  useEffect(() => {
    if (hotelId) {
      fetchDepartments();
      if (activeView === 'runs') {
        fetchPayrollRuns();
      } else if (activeView === 'payslips') {
        fetchPayslips();
      } else if (activeView === 'salaries') {
        fetchStaffSalaries();
      }
    }
  }, [hotelId, token, activeView, selectedMonth, selectedYear, selectedDepartment]);

  // API Calls
  const fetchDepartments = async () => {
    try {
      const authConfig = await getAuthConfig();
      if (!authConfig.headers?.Authorization) return;

      const response = await axios.get(`${API_BASE_URL}/departments`, authConfig);
      const rows = response.data?.data || response.data || [];
      setDepartments(rows.map((dept) => ({
        id: dept.department_id ?? dept.id,
        name: dept.department_name ?? dept.name
      })));
    } catch (err) {
      console.error('Failed to fetch departments:', err);
    }
  };

  const fetchPayrollRuns = async () => {
    setLoading(true);
    setError(null);
    try {
      const authConfig = await getAuthConfig();
      if (!authConfig.headers?.Authorization) {
        setError('Authentication required. Please log in again.');
        return;
      }

      const response = await axios.get(`${API_BASE_URL}/payroll/hotel/${hotelId}/runs`, authConfig);
      const rows = response.data?.data || response.data || [];
      setPayrollRuns(rows.map((run) => ({
        id: run.payroll_run_id ?? run.id,
        period_start: run.pay_period_start ?? run.period_start,
        period_end: run.pay_period_end ?? run.period_end,
        department_name: run.department_name,
        staff_count: Number(run.total_payslips || run.staff_count || 0),
        total_amount: parseFloat(run.total_net || run.total_amount || 0),
        status: run.status === 'draft' ? 'pending' : (run.status === 'paid' ? 'completed' : run.status),
        processed_at: run.paid_at || run.processed_at
      })));
    } catch (err) {
      setError('Failed to load payroll runs');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const fetchPayslips = async () => {
    setLoading(true);
    setError(null);
    try {
      const authConfig = await getAuthConfig();
      if (!authConfig.headers?.Authorization) {
        setError('Authentication required. Please log in again.');
        return;
      }

      const url = selectedDepartment === 'all'
        ? `${API_BASE_URL}/payroll/hotel/${hotelId}/payslips?month=${selectedMonth}&year=${selectedYear}`
        : `${API_BASE_URL}/payroll/hotel/${hotelId}/payslips?month=${selectedMonth}&year=${selectedYear}&department_id=${selectedDepartment}`;
      
      const response = await axios.get(url, authConfig);
      setPayslips(response.data?.data || response.data || []);
    } catch (err) {
      setError('Failed to load payslips');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const fetchStaffSalaries = async () => {
    setLoading(true);
    setError(null);
    try {
      const authConfig = await getAuthConfig();
      if (!authConfig.headers?.Authorization) {
        setError('Authentication required. Please log in again.');
        return;
      }

      const url = selectedDepartment === 'all'
        ? `${API_BASE_URL}/payroll/hotel/${hotelId}/salaries`
        : `${API_BASE_URL}/payroll/hotel/${hotelId}/salaries?department_id=${selectedDepartment}`;

      const response = await axios.get(url, authConfig);
      setStaffSalaries(response.data?.data || response.data || []);
    } catch (err) {
      setError('Failed to load staff salaries');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleCreatePayrollRun = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const authConfig = await getAuthConfig();
      if (!authConfig.headers?.Authorization) {
        setError('Authentication required. Please log in again.');
        return;
      }

      await axios.post(
        `${API_BASE_URL}/payroll/hotel/${hotelId}/run`,
        {
          run_name: `Payroll ${runFormData.period_start} to ${runFormData.period_end}`,
          pay_period_start: runFormData.period_start,
          pay_period_end: runFormData.period_end,
          payment_date: runFormData.period_end,
          notes: runFormData.department_id ? `Department ID: ${runFormData.department_id}` : null
        },
        authConfig
      );

      setShowRunModal(false);
      setRunFormData({ period_start: '', period_end: '', department_id: '' });
      fetchPayrollRuns();
    } catch (err) {
      setError('Failed to create payroll run');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleProcessPayroll = async (runId) => {
    if (!window.confirm('Are you sure you want to process this payroll run? This will generate payslips for all staff.')) {
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const authConfig = await getAuthConfig();
      if (!authConfig.headers?.Authorization) {
        setError('Authentication required. Please log in again.');
        return;
      }

      await axios.post(
        `${API_BASE_URL}/payroll/run/${runId}/generate`,
        {
          department_id: selectedDepartment === 'all' ? null : selectedDepartment
        },
        authConfig
      );

      fetchPayrollRuns();
    } catch (err) {
      setError('Failed to process payroll');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateSalary = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const authConfig = await getAuthConfig();
      if (!authConfig.headers?.Authorization) {
        setError('Authentication required. Please log in again.');
        return;
      }

      await axios.put(
        `${API_BASE_URL}/payroll/staff/${editingStaff.staff_id}/salary`,
        salaryFormData,
        authConfig
      );

      setShowSalaryModal(false);
      setEditingStaff(null);
      setSalaryFormData({ base_salary: '', allowances: '', deductions: '', effective_date: '' });
      fetchStaffSalaries();
    } catch (err) {
      setError('Failed to update salary');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadPayslip = async (payslipId) => {
    try {
      const authConfig = await getAuthConfig();
      if (!authConfig.headers?.Authorization) {
        setError('Authentication required. Please log in again.');
        return;
      }

      const response = await axios.get(
        `${API_BASE_URL}/payroll/payslips/${payslipId}/download`,
        {
          ...authConfig,
          responseType: 'blob'
        }
      );

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `payslip_${payslipId}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      setError('Failed to download payslip');
      console.error(err);
    }
  };

  // Helper functions
  const formatCurrency = (amount) => {
    if (!amount) return '$0.00';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2
    }).format(amount);
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const getStatusBadge = (status) => {
    const statusMap = {
      pending: { icon: Clock, color: 'warning', label: 'Pending' },
      processing: { icon: RefreshCw, color: 'info', label: 'Processing' },
      completed: { icon: CheckCircle, color: 'success', label: 'Completed' },
      failed: { icon: XCircle, color: 'danger', label: 'Failed' }
    };

    const config = statusMap[status] || statusMap.pending;
    const Icon = config.icon;

    return (
      <span className={`status-badge ${config.color}`}>
        <Icon size={14} />
        {config.label}
      </span>
    );
  };

  const openSalaryModal = (staff) => {
    setEditingStaff(staff);
    setSalaryFormData({
      base_salary: staff.base_salary || '',
      allowances: staff.allowances || '',
      deductions: staff.deductions || '',
      effective_date: new Date().toISOString().split('T')[0]
    });
    setShowSalaryModal(true);
  };

  const openRunModal = () => {
    const today = new Date();
    const firstDay = new Date(today.getFullYear(), today.getMonth(), 1);
    const lastDay = new Date(today.getFullYear(), today.getMonth() + 1, 0);

    setRunFormData({
      period_start: firstDay.toISOString().split('T')[0],
      period_end: lastDay.toISOString().split('T')[0],
      department_id: selectedDepartment === 'all' ? '' : selectedDepartment
    });
    setShowRunModal(true);
  };

  const filteredPayslips = payslips.filter(payslip =>
    payslip.staff_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    payslip.staff_id?.toString().includes(searchQuery)
  );

  const filteredSalaries = staffSalaries.filter(staff => {
    const matchesSearch = staff.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         staff.staff_id?.toString().includes(searchQuery);
    const matchesDept = selectedDepartment === 'all' || staff.department_id === parseInt(selectedDepartment);
    return matchesSearch && matchesDept;
  });

  const calculateTotalPayroll = () => {
    return payrollRuns
      .filter(run => run.status === 'completed')
      .reduce((sum, run) => sum + (parseFloat(run.total_amount) || 0), 0);
  };

  const calculateMonthlyPayroll = () => {
    return payslips.reduce((sum, slip) => sum + (parseFloat(slip.net_amount) || 0), 0);
  };

  return (
    <div className="payroll-tab">
      {/* Header */}
      <div className="payroll-header">
        <div className="header-left">
          <h2>
            <DollarSign size={24} />
            Payroll Management
          </h2>
          <p>Process payroll, manage salaries, and generate payslips</p>
        </div>
        <div className="header-actions">
          <button className="btn-icon" onClick={() => {
            if (activeView === 'runs') fetchPayrollRuns();
            else if (activeView === 'payslips') fetchPayslips();
            else fetchStaffSalaries();
          }}>
            <RefreshCw size={18} />
          </button>
          {activeView === 'runs' && (
            <button className="btn-primary" onClick={openRunModal}>
              <Play size={18} />
              New Payroll Run
            </button>
          )}
        </div>
      </div>

      {/* View Switcher */}
      <div className="view-switcher">
        <button
          className={activeView === 'runs' ? 'active' : ''}
          onClick={() => setActiveView('runs')}
        >
          <Calendar size={18} />
          Payroll Runs
        </button>
        <button
          className={activeView === 'payslips' ? 'active' : ''}
          onClick={() => setActiveView('payslips')}
        >
          <FileText size={18} />
          Payslips
        </button>
        <button
          className={activeView === 'salaries' ? 'active' : ''}
          onClick={() => setActiveView('salaries')}
        >
          <Users size={18} />
          Staff Salaries
        </button>
      </div>

      {/* Error Display */}
      {error && (
        <div className="error-banner">
          <AlertCircle size={20} />
          <span>{error}</span>
          <button onClick={() => setError(null)}><XCircle size={16} /></button>
        </div>
      )}

      {/* Summary Cards */}
      <div className="summary-cards">
        <div className="summary-card">
          <div className="card-icon total">
            <DollarSign size={24} />
          </div>
          <div className="card-content">
            <h3>{formatCurrency(calculateTotalPayroll())}</h3>
            <p>Total Processed</p>
          </div>
        </div>
        <div className="summary-card">
          <div className="card-icon monthly">
            <TrendingUp size={24} />
          </div>
          <div className="card-content">
            <h3>{formatCurrency(calculateMonthlyPayroll())}</h3>
            <p>Current Period</p>
          </div>
        </div>
        <div className="summary-card">
          <div className="card-icon staff">
            <Users size={24} />
          </div>
          <div className="card-content">
            <h3>{staffSalaries.length}</h3>
            <p>Staff Members</p>
          </div>
        </div>
      </div>

      {/* Payroll Runs View */}
      {activeView === 'runs' && (
        <div className="runs-view">
          {loading ? (
            <div className="loading-state">
              <RefreshCw className="spin" size={32} />
              <p>Loading payroll runs...</p>
            </div>
          ) : (
            <div className="runs-list">
              {payrollRuns.map(run => (
                <div key={run.id} className={`run-card status-${run.status}`}>
                  <div className="run-header">
                    <div className="run-info">
                      <h3>Payroll Run #{run.id}</h3>
                      <p className="run-period">
                        {formatDate(run.period_start)} - {formatDate(run.period_end)}
                      </p>
                    </div>
                    {getStatusBadge(run.status)}
                  </div>

                  <div className="run-stats">
                    <div className="stat">
                      <Users size={16} />
                      <span>{run.staff_count || 0} Staff</span>
                    </div>
                    <div className="stat">
                      <DollarSign size={16} />
                      <span>{formatCurrency(run.total_amount)}</span>
                    </div>
                    {run.department_name && (
                      <div className="stat">
                        <Filter size={16} />
                        <span>{run.department_name}</span>
                      </div>
                    )}
                  </div>

                  <div className="run-actions">
                    {run.status === 'pending' && (
                      <button 
                        className="btn-success"
                        onClick={() => handleProcessPayroll(run.id)}
                      >
                        <Play size={16} />
                        Process Payroll
                      </button>
                    )}
                    {run.status === 'completed' && (
                      <button className="btn-secondary" onClick={() => {
                        setSelectedMonth(new Date(run.period_start).getMonth() + 1);
                        setSelectedYear(new Date(run.period_start).getFullYear());
                        setActiveView('payslips');
                      }}>
                        <Eye size={16} />
                        View Payslips
                      </button>
                    )}
                  </div>

                  {run.processed_at && (
                    <div className="run-footer">
                      <span>Processed on {formatDate(run.processed_at)}</span>
                    </div>
                  )}
                </div>
              ))}

              {payrollRuns.length === 0 && (
                <div className="empty-state">
                  <Calendar size={48} />
                  <h3>No payroll runs</h3>
                  <p>Create your first payroll run to get started</p>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Payslips View */}
      {activeView === 'payslips' && (
        <div className="payslips-view">
          <div className="view-controls">
            <div className="filters-group">
              <div className="search-box">
                <Search size={18} />
                <input
                  type="text"
                  placeholder="Search staff..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </div>
              <select
                value={selectedMonth}
                onChange={(e) => setSelectedMonth(parseInt(e.target.value))}
              >
                {Array.from({ length: 12 }, (_, i) => (
                  <option key={i + 1} value={i + 1}>
                    {new Date(2000, i).toLocaleString('default', { month: 'long' })}
                  </option>
                ))}
              </select>
              <select
                value={selectedYear}
                onChange={(e) => setSelectedYear(parseInt(e.target.value))}
              >
                {Array.from({ length: 5 }, (_, i) => {
                  const year = new Date().getFullYear() - i;
                  return <option key={year} value={year}>{year}</option>;
                })}
              </select>
              <select
                value={selectedDepartment}
                onChange={(e) => setSelectedDepartment(e.target.value)}
              >
                <option value="all">All Departments</option>
                {departments.map(dept => (
                  <option key={dept.id} value={dept.id}>{dept.name}</option>
                ))}
              </select>
            </div>
          </div>

          {loading ? (
            <div className="loading-state">
              <RefreshCw className="spin" size={32} />
              <p>Loading payslips...</p>
            </div>
          ) : (
            <div className="payslips-list">
              {filteredPayslips.map(payslip => (
                <div key={payslip.id} className="payslip-card">
                  <div className="payslip-header" onClick={() => setExpandedPayslip(expandedPayslip === payslip.id ? null : payslip.id)}>
                    <div className="staff-info">
                      <div className="staff-avatar">
                        <Users size={20} />
                      </div>
                      <div>
                        <h4>{payslip.staff_name}</h4>
                        <p>{payslip.department_name}</p>
                      </div>
                    </div>
                    <div className="payslip-summary">
                      <div className="net-amount">
                        <span className="label">Net Pay</span>
                        <span className="amount">{formatCurrency(payslip.net_amount)}</span>
                      </div>
                      <button className="expand-btn">
                        {expandedPayslip === payslip.id ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
                      </button>
                    </div>
                  </div>

                  {expandedPayslip === payslip.id && (
                    <div className="payslip-details">
                      <div className="details-grid">
                        <div className="detail-item">
                          <span className="detail-label">Base Salary</span>
                          <span className="detail-value">{formatCurrency(payslip.base_salary)}</span>
                        </div>
                        <div className="detail-item">
                          <span className="detail-label">Allowances</span>
                          <span className="detail-value positive">{formatCurrency(payslip.allowances)}</span>
                        </div>
                        <div className="detail-item">
                          <span className="detail-label">Deductions</span>
                          <span className="detail-value negative">-{formatCurrency(payslip.deductions)}</span>
                        </div>
                        <div className="detail-item">
                          <span className="detail-label">Bonuses</span>
                          <span className="detail-value positive">{formatCurrency(payslip.bonuses)}</span>
                        </div>
                        <div className="detail-item">
                          <span className="detail-label">Overtime</span>
                          <span className="detail-value">{payslip.overtime_hours || 0} hrs</span>
                        </div>
                        <div className="detail-item">
                          <span className="detail-label">Period</span>
                          <span className="detail-value">
                            {formatDate(payslip.period_start)} - {formatDate(payslip.period_end)}
                          </span>
                        </div>
                      </div>
                      <div className="payslip-actions">
                        <button 
                          className="btn-download"
                          onClick={() => handleDownloadPayslip(payslip.id)}
                        >
                          <Download size={16} />
                          Download PDF
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              ))}

              {filteredPayslips.length === 0 && (
                <div className="empty-state">
                  <FileText size={48} />
                  <h3>No payslips found</h3>
                  <p>No payslips for the selected period</p>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Staff Salaries View */}
      {activeView === 'salaries' && (
        <div className="salaries-view">
          <div className="view-controls">
            <div className="filters-group">
              <div className="search-box">
                <Search size={18} />
                <input
                  type="text"
                  placeholder="Search staff..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </div>
              <select
                value={selectedDepartment}
                onChange={(e) => setSelectedDepartment(e.target.value)}
              >
                <option value="all">All Departments</option>
                {departments.map(dept => (
                  <option key={dept.id} value={dept.id}>{dept.name}</option>
                ))}
              </select>
            </div>
          </div>

          {loading ? (
            <div className="loading-state">
              <RefreshCw className="spin" size={32} />
              <p>Loading staff salaries...</p>
            </div>
          ) : (
            <div className="salaries-table">
              <table>
                <thead>
                  <tr>
                    <th>Staff Member</th>
                    <th>Department</th>
                    <th>Base Salary</th>
                    <th>Allowances</th>
                    <th>Deductions</th>
                    <th>Net Salary</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredSalaries.map(staff => (
                    <tr key={staff.staff_id}>
                      <td>
                        <div className="staff-cell">
                          <Users size={16} />
                          <span>{staff.name}</span>
                        </div>
                      </td>
                      <td>{staff.department_name}</td>
                      <td>{formatCurrency(staff.base_salary)}</td>
                      <td className="positive">{formatCurrency(staff.allowances)}</td>
                      <td className="negative">{formatCurrency(staff.deductions)}</td>
                      <td className="net-salary">
                        {formatCurrency((parseFloat(staff.base_salary) || 0) + 
                                       (parseFloat(staff.allowances) || 0) - 
                                       (parseFloat(staff.deductions) || 0))}
                      </td>
                      <td>
                        <button 
                          className="btn-edit"
                          onClick={() => openSalaryModal(staff)}
                        >
                          Update
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {filteredSalaries.length === 0 && (
                <div className="empty-state">
                  <Users size={48} />
                  <h3>No staff found</h3>
                  <p>No staff members match your search</p>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Payroll Run Modal */}
      {showRunModal && (
        <div className="modal-overlay" onClick={() => setShowRunModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Create Payroll Run</h3>
              <button onClick={() => setShowRunModal(false)}>
                <XCircle size={20} />
              </button>
            </div>
            <form onSubmit={handleCreatePayrollRun}>
              <div className="form-row">
                <div className="form-group">
                  <label>Period Start *</label>
                  <input
                    type="date"
                    value={runFormData.period_start}
                    onChange={(e) => setRunFormData({ ...runFormData, period_start: e.target.value })}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Period End *</label>
                  <input
                    type="date"
                    value={runFormData.period_end}
                    onChange={(e) => setRunFormData({ ...runFormData, period_end: e.target.value })}
                    required
                  />
                </div>
              </div>
              <div className="form-group">
                <label>Department (Optional)</label>
                <select
                  value={runFormData.department_id}
                  onChange={(e) => setRunFormData({ ...runFormData, department_id: e.target.value })}
                >
                  <option value="">All Departments</option>
                  {departments.map(dept => (
                    <option key={dept.id} value={dept.id}>{dept.name}</option>
                  ))}
                </select>
              </div>
              <div className="modal-actions">
                <button type="button" className="btn-secondary" onClick={() => setShowRunModal(false)}>
                  Cancel
                </button>
                <button type="submit" className="btn-primary" disabled={loading}>
                  {loading ? 'Creating...' : 'Create Run'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Salary Update Modal */}
      {showSalaryModal && editingStaff && (
        <div className="modal-overlay" onClick={() => setShowSalaryModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Update Salary - {editingStaff.name}</h3>
              <button onClick={() => setShowSalaryModal(false)}>
                <XCircle size={20} />
              </button>
            </div>
            <form onSubmit={handleUpdateSalary}>
              <div className="form-group">
                <label>Base Salary *</label>
                <input
                  type="number"
                  value={salaryFormData.base_salary}
                  onChange={(e) => setSalaryFormData({ ...salaryFormData, base_salary: e.target.value })}
                  placeholder="Enter base salary"
                  min="0"
                  step="0.01"
                  required
                />
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Allowances</label>
                  <input
                    type="number"
                    value={salaryFormData.allowances}
                    onChange={(e) => setSalaryFormData({ ...salaryFormData, allowances: e.target.value })}
                    placeholder="0.00"
                    min="0"
                    step="0.01"
                  />
                </div>
                <div className="form-group">
                  <label>Deductions</label>
                  <input
                    type="number"
                    value={salaryFormData.deductions}
                    onChange={(e) => setSalaryFormData({ ...salaryFormData, deductions: e.target.value })}
                    placeholder="0.00"
                    min="0"
                    step="0.01"
                  />
                </div>
              </div>
              <div className="form-group">
                <label>Effective Date *</label>
                <input
                  type="date"
                  value={salaryFormData.effective_date}
                  onChange={(e) => setSalaryFormData({ ...salaryFormData, effective_date: e.target.value })}
                  required
                />
              </div>
              <div className="modal-actions">
                <button type="button" className="btn-secondary" onClick={() => setShowSalaryModal(false)}>
                  Cancel
                </button>
                <button type="submit" className="btn-primary" disabled={loading}>
                  {loading ? 'Updating...' : 'Update Salary'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default PayrollTab;